import {
  Text,
  IconCircleInfo,
  GreyGrey100,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  Primary13Regular,
  Primary14SemiBold,
  Tooltip,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { Fragment } from 'react'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { Price, Product } from 'common/drawers/PricingEditor/domain'
import {
  formatXeroMappingOption,
  XERO_MAPPING_LABELS
} from 'Products/constants/xeroMapping.constants'
import { useShouldShowXeroMappings } from 'Products/hooks/useShouldShowXeroMappings'
import { useAdditionalInfo } from './useAdditionalInfo'

const XERO_LEDGER_DOCS_URL =
  'https://docs.sequencehq.com/connectors/xero#ledger-accounts'

export const AdditionalInfo = ({
  price,
  product
}: {
  price: Price
  product?: Product
}) => {
  const { validIntegrationIds } = useAdditionalInfo({
    price
  })
  const showXeroMappings = useShouldShowXeroMappings()
  const { data } = useQuery(
    dashboardv99990101Client.getXeroLedgerAccountMappings,
    undefined,
    { enabled: showXeroMappings }
  )

  const mappings = data?.items ?? []
  const deferredMapping = mappings.find(
    m => m.id === product?.deferredRevenueXeroAccountMappingId
  )
  const recognizedMapping = mappings.find(
    m => m.id === product?.recognizedRevenueXeroAccountMappingId
  )
  const unbilledMapping = mappings.find(
    m => m.id === product?.unbilledRevenueXeroAccountMappingId
  )

  const hasAnyXeroMapping = Boolean(
    deferredMapping || recognizedMapping || unbilledMapping
  )

  const hasXeroContent =
    'Xero' in validIntegrationIds || (showXeroMappings && hasAnyXeroMapping)

  if (Object.keys(validIntegrationIds).length === 0 && !hasAnyXeroMapping) {
    return null
  }

  return (
    <Flex flexDirection="column" mt={6}>
      <Flex alignItems="center" gap="4px">
        <Text {...Primary14SemiBold} color={GreyGrey100}>
          Additional information
        </Text>
        {hasXeroContent && (
          <Tooltip label="Deferred and recognized revenue accounts drive revenue recognition journals. The Xero ledger account is used when syncing invoices to Xero. Click to learn more.">
            <a
              href={XERO_LEDGER_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="priceEditor.review.additionalInfo.xeroDocsLink"
            >
              <IconCircleInfo color={GreyGrey60} />
            </a>
          </Tooltip>
        )}
      </Flex>
      <Grid templateColumns="200px 1fr" gap={3} mt={4} color={GreyGrey70}>
        {Object.keys(validIntegrationIds).map((service, index) => (
          <Fragment key={index}>
            <Text {...Primary13Regular}>{service} ledger account</Text>
            <Text {...Primary13Regular} color={GreyGrey90}>
              {formatXeroMappingOption(
                validIntegrationIds[service].code,
                validIntegrationIds[service].name
              )}
            </Text>
          </Fragment>
        ))}
        {deferredMapping && (
          <>
            <Text {...Primary13Regular}>
              {XERO_MAPPING_LABELS.DEFERRED_REVENUE}
            </Text>
            <Text
              {...Primary13Regular}
              color={GreyGrey90}
              data-testid="priceEditor.review.deferredRevenueXeroAccount"
            >
              {formatXeroMappingOption(
                deferredMapping.xeroLedgerAccountCode,
                deferredMapping.xeroLedgerAccountName
              )}
            </Text>
          </>
        )}
        {recognizedMapping && (
          <>
            <Text {...Primary13Regular}>
              {XERO_MAPPING_LABELS.RECOGNIZED_REVENUE}
            </Text>
            <Text
              {...Primary13Regular}
              color={GreyGrey90}
              data-testid="priceEditor.review.recognizedRevenueXeroAccount"
            >
              {formatXeroMappingOption(
                recognizedMapping.xeroLedgerAccountCode,
                recognizedMapping.xeroLedgerAccountName
              )}
            </Text>
          </>
        )}
        {unbilledMapping && (
          <>
            <Text {...Primary13Regular}>
              {XERO_MAPPING_LABELS.UNBILLED_REVENUE}
            </Text>
            <Text
              {...Primary13Regular}
              color={GreyGrey90}
              data-testid="priceEditor.review.unbilledRevenueXeroAccount"
            >
              {formatXeroMappingOption(
                unbilledMapping.xeroLedgerAccountCode,
                unbilledMapping.xeroLedgerAccountName
              )}
            </Text>
          </>
        )}
      </Grid>
    </Flex>
  )
}
